import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";

export type InviteCodeType = "assignment" | "course" | "group" | "register";

export interface InviteRecipientPayload {
    invite_code_id?: number | null;
    type: "email" | "phone";
    recipient: string;
    name?: string | null;
    is_activated?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface InviteCodePayload {
    id?: number | null;
    owner_id?: number | null;
    product_id?: number | null;
    type: InviteCodeType;
    name?: string | null;
    description?: string | null;
    code: string;
    settings?: Record<string, any>;
    limits?: string | null;
    is_revoked?: boolean;
    expires_in?: string | null;
    expired_at?: string | null;

    /**
     * Backend may return/accept either boolean or 0/1 number for this field.
     * UI uses 1/0 to match server examples.
     */
    form_enabled?: boolean | number;

    form_url?: string | null;
    form_name?: string | null;
    form_description?: string | null;

    /**
     * Backend sometimes returns [] for empty objects. Keep it flexible.
     */
    form_data?: Record<string, any> | any[];

    form_logo?: string | null;
    removed_users?: number[];
    usages_count?: number;
    assignments?: number[];
    courses?: number[];
    classgroups?: number[];
    recipients?: InviteRecipientPayload[];
}

export interface InviteCodeEntity extends InviteCodePayload {
    id: number;
    owner_id: number | null;
    usages_count: number;
}

/**
 * Фактический ответ бэка оборачивает InviteCodeEntity
 * в envelope { status, status_code, entity }.
 */
export interface InviteCodeResponseEnvelope {
    status: string;
    status_code: string;
    entity: InviteCodeEntity;
}

/**
 *  Список инвайт-кодов ("/invite-codes?page=1").
 *  Структура повторяет envelope с полем entities, аналогично Assignments/Quizzes.
 */
export interface InviteCodeListItem {
    id: number;
    type: InviteCodeType | string;
    name: string | null;
    description: string | null;
    code?: string | null;
    usages_count: number;
    expired_at: string | null;
}

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

export interface InviteCodesEntities {
    current_page: number;
    data: InviteCodeListItem[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

export interface GetInviteCodesResponse {
    status: string;
    status_code: string;
    entities: InviteCodesEntities;
}

export interface GetInviteCodesParams {
    page?: number;
    limit?: number;
    type?: InviteCodeType | "all";
    orderBy?: "asc" | "desc";
    query?: string;
    nullable?: 0 | 1 | boolean;
}

export interface BulkDeleteInviteCodesResponse {
    success: boolean;
    data: {
        message: string;
        deleted_count: number;
    };
}

export const inviteCodeApi = createApi({
    reducerPath: "inviteCodeApi",
    baseQuery: privateBaseQuery(),
    tagTypes: ["InviteCode"],
    endpoints: (builder) => ({
        getInviteCodes: builder.query<InviteCodesEntities, GetInviteCodesParams | void>({
            query: (params = { page: 1 }) => ({
                url: "/invite-codes",
                method: "GET",
                params,
                headers: {
                    "X-Requested-Fields": "id,type,name,description,code,usages_count,expired_at",
                },
            }),
            transformResponse: (response: GetInviteCodesResponse) => response.entities,
            providesTags: ["InviteCode"],
        }),
        getInviteCodeById: builder.query<InviteCodeEntity, number>({
            query: (id) => ({
                url: `/invite-codes/${id}`,
                method: "GET",
                params: {
                    extended: true,
                },
                headers: {
                    "X-Requested-Fields":
                        "id,owner_id,product_id,type,name,description,code,limits,is_revoked,expires_in,expired_at,settings,form_enabled,form_url,form_logo,form_name,form_description,form_data,assignments:id,assignments:status,assignments:type,assignments:name,assignments:lesson:name,assignments:quiz:name,assignments:class:name,courses:id,courses:name,courses:short_description,courses:image,courses:color,classgroups:id,classgroups:name,classgroups:description,classgroups:image,classgroups:color,classgroups:students_count,recipients:invite_code_id,recipients:type,recipients:recipient,recipients:name,recipients:is_activated,recipients:created_at,recipients:updated_at",
                },
            }),
            transformResponse: (response: InviteCodeResponseEnvelope) => response.entity,
            providesTags: ["InviteCode"],
        }),
        createInviteCode: builder.mutation<InviteCodeResponseEnvelope, InviteCodePayload>({
            query: (payload) => ({
                url: "/invite-codes",
                method: "POST",
                data: payload,
                params: {
                    extended: true,
                },
            }),
            invalidatesTags: ["InviteCode"],
        }),
        updateInviteCode: builder.mutation<InviteCodeResponseEnvelope, InviteCodePayload>({
            query: ({ id, ...payload }) => ({
                url: `/invite-codes/${id}`,
                method: "PUT",
                data: payload,
                params: {
                    extended: true,
                },
            }),
            invalidatesTags: ["InviteCode"],
        }),

        deleteInviteCode: builder.mutation<void, number>({
            query: (id) => ({
                url: `/invite-codes/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["InviteCode"],
        }),
        bulkDeleteInviteCodes: builder.mutation<BulkDeleteInviteCodesResponse, number[]>({
            query: (ids) => ({
                url: "/invite-codes/bulk-delete",
                method: "POST",
                data: { ids },
            }),
            invalidatesTags: ["InviteCode"],
        }),
    }),
});

export const {
    useCreateInviteCodeMutation,
    useDeleteInviteCodeMutation,
    useBulkDeleteInviteCodesMutation,
    useGetInviteCodesQuery,
    useGetInviteCodeByIdQuery,
    useLazyGetInviteCodeByIdQuery,
    useUpdateInviteCodeMutation,
} = inviteCodeApi;
